/**
 * SplashScreen — animated Earth loading screen.
 * Reuses the existing useSpaceLoader canvas animation.
 * Stitch SPACEMAN_CRAFT_V2.0 design system.
 *
 * Props:
 *   loadingComplete  bool    — when true, progress reaches 100% and onComplete fires
 *   onComplete       fn()    — called when done
 */
import { useRef, useState, useEffect, useCallback } from 'react';
import useSpaceLoader from '../SpaceLoader/useSpaceLoader';
import './SplashScreen.css';

const LOAD_MSGS = [
  'BOOTING CORE_KERNEL v2.0.4...',
  'INITIALISING SYSTEMS...',
  'ESTABLISHING UPLINK...',
  'SYNCING ORBITAL DATA...',
  'ENCRYPTING DOWNLINK TUNNEL [AES-256]',
  'AUTHENTICATING MISSION COMMANDER...',
  'SYSTEM ONLINE',
];

const MET_START = Date.now();

function formatMET(ms) {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60) % 60;
  const h = Math.floor(s / 3600) % 24;
  const d = Math.floor(s / 86400);
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d)}:${pad(h)}:${pad(m)}:${pad(s % 60)}`;
}

export default function SplashScreen({ loadingComplete, onComplete }) {
  const { canvasRef, score, loadProgress } = useSpaceLoader({
    loadingComplete,
    onComplete,
    minDisplayMs: 1200,
  });

  const wrapRef   = useRef(null);
  const [met,     setMet]     = useState('000:00:00:00');
  const [ripples, setRipples] = useState([]);

  // MET clock
  useEffect(() => {
    const id = setInterval(() => {
      setMet(formatMET(Date.now() - MET_START));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Active terminal line index
  const msgIdx = Math.min(
    Math.floor((loadProgress / 100) * (LOAD_MSGS.length - 1)),
    LOAD_MSGS.length - 1
  );

  // Ripple on click
  const handleClick = useCallback((e) => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const id = Date.now() + Math.random();
    setRipples(prev => [...prev, { id, x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    setTimeout(() => setRipples(prev => prev.filter(r => r.id !== id)), 650);
  }, []);

  const isDone = loadProgress >= 100;

  return (
    <div className="spl-wrap" ref={wrapRef} onClick={handleClick}>
      {/* Grid + glow backgrounds */}
      <div className="spl-grid" />
      <div className="spl-glow" />

      {/* Canvas (Earth + satellites animation) */}
      <canvas ref={canvasRef} className="spl-canvas" />

      {/* Corner brackets */}
      <div className="spl-corner spl-corner--tl" />
      <div className="spl-corner spl-corner--tr" />
      <div className="spl-corner spl-corner--bl" />
      <div className="spl-corner spl-corner--br" />

      {/* HUD top bar */}
      <div className="spl-hud-top">
        <div className="spl-hud-status">
          <div className="spl-hud-pulse">
            <span className="spl-pulse-dot" />
            <span style={{ fontSize: 9, color: '#65d4fa', letterSpacing: '0.2em', textTransform: 'uppercase' }}>
              System_Live
            </span>
          </div>
          <div className="spl-hud-met">
            MET: <span>{met}</span>
          </div>
        </div>
        <div className="spl-hud-auth">
          <div className="spl-hud-auth-label">Auth_Status</div>
          <div className="spl-hud-auth-value">
            {isDone ? 'AUTHENTICATED' : 'AWAITING_PROTOCOL'}
          </div>
        </div>
      </div>

      {/* Branding panel */}
      <div className="spl-brand">
        <span className="spl-material-icon spl-brand-icon">satellite_alt</span>
        <div className="spl-brand-name">SPACEMANCRAFT</div>
        <div className="spl-brand-sub">Orbital_Operating_System // v2.4.0</div>
        <div className="spl-brand-ver">SATELLITE-CLASS OS · DEEP SPACE NETWORK</div>

        {/* Downlink / signal */}
        <div style={{ display: 'flex', gap: 24, opacity: 0.5 }}>
          <div>
            <div style={{ fontSize: 8, color: '#879298', letterSpacing: '0.2em', marginBottom: 3 }}>DOWNLINK_RATE</div>
            <div style={{ fontSize: 11, color: '#bdc8ce', letterSpacing: '0.12em' }}>482.1 GB/S</div>
          </div>
          <div>
            <div style={{ fontSize: 8, color: '#879298', letterSpacing: '0.2em', marginBottom: 3 }}>SIGNAL_LOCK</div>
            <div style={{ fontSize: 11, color: '#65d4fa', letterSpacing: '0.12em' }}>STABLE</div>
          </div>
        </div>
      </div>

      {/* Orbital vectors (left bottom) */}
      <div className="spl-vectors">
        <div className="spl-vectors-label">ORBITAL_VECTORS</div>
        <div className="spl-vectors-val">
          VEL: 7.67 KM/S<br />
          ALT: 450.0 KM<br />
          INC: 97.4°
        </div>
      </div>

      {/* Bottom UI */}
      <div className="spl-bottom">
        {/* Satellite counter */}
        <div className="spl-sat-count">
          <span className="spl-sat-badge">
            SMC-1 · SATELLITES: {score}
          </span>
        </div>

        {score === 0 && !isDone && (
          <div className="spl-tap-hint">TAP TO LAUNCH SATELLITE</div>
        )}

        {/* Progress bar */}
        <div className="spl-bar-track">
          <div className="spl-bar-fill" style={{ width: `${loadProgress}%` }} />
        </div>
        <div className="spl-bar-meta">
          <div className="spl-bar-msg">
            {isDone ? 'FIREBASE CONNECTED' : LOAD_MSGS[msgIdx]}
          </div>
          <div className="spl-bar-pct">{Math.floor(loadProgress)}%</div>
        </div>

        {/* Terminal output */}
        <div className="spl-terminal">
          <div className="spl-terminal-header">
            <span className="spl-material-icon" style={{ fontSize: 14, color: '#65d4fa' }}>terminal</span>
            <span className="spl-terminal-title">SYS_LOG_STREAM</span>
          </div>
          {LOAD_MSGS.slice(0, Math.min(msgIdx + 1, LOAD_MSGS.length)).map((msg, i) => (
            <div
              key={i}
              className={`spl-terminal-line ${
                i === msgIdx ? 'spl-terminal-line--active' : 'spl-terminal-line--dim'
              }`}
            >
              &gt; {msg}
            </div>
          ))}
        </div>
      </div>

      {/* HUD bottom bar */}
      <div className="spl-hud-bottom">
        <div className="spl-hud-coord">
          Lat: 28.5729° N<br />
          Long: 80.6490° W
        </div>
        <div className="spl-hud-secure">
          Secure Environment
          <div className="spl-hud-rule">
            <span style={{ width: 16 }} />
            <span style={{ width: 32 }} />
            <span style={{ width: 8 }} />
          </div>
        </div>
      </div>

      {/* Ripples */}
      {ripples.map(r => (
        <div
          key={r.id}
          className="spl-ripple"
          style={{ left: r.x, top: r.y }}
        />
      ))}
    </div>
  );
}
